import React, { useCallback, useContext, useMemo, useState } from 'react'
import styled from 'styled-components'
import { Column, FlexContainer, Heading3, Page, Row, Text } from '../../../components/layout'
import {
  ActionButtons,
  JobDayFilter,
  JobTypeFilter,
  PriorityFilter,
  StationFilter,
  StatusFilter,
  TowingJobFilter,
  VisibilityFilter,
  FilterModal,
  ToggleAll,
  initialValues as filterInitialValues,
} from '../../../components/TowingJobFilter/TowingJobFilter'
import { OperatorFilter } from '../../../components/TowingJobFilter/filters/OperatorFilter'
import { useTowingJobFilters } from '../../../components/TowingJobFilter/useTowingJobFilters'
import { Button } from '../../../components/Button'
import Link from '../../../components/Link'
import { useHistoryPush } from '../../../components/router'
import { usePreferences, useSettings } from '../../Parameters'
import { StateStore } from '../../StoreProvider'
import { useRepairShops } from '../../TowingRecordPage/components/routes/subforms/FinishRoute/options'
import { messages } from '../messages'
import { removeLocalStorageTowingJobFilters, setLocalStorageTowingJobFilters } from '../../../graphql-client/storedState'
import { useDebounce } from '../../../util/useDebounce'
import { ButtonContainer } from '../../TowingRecordPage/components/routes/subforms/styled'
import { formatMessageSafe } from '../../../util/translateInputOptions'
import { useIntl } from 'react-intl'
import TowingJobsList from './TowingJobsList'
import { TOWING_JOBS_SEARCH_QUERY, TowingJobsResponse } from '../TowingJobsQuery'
import { useQuery } from '@apollo/client/react'

const AddButton = styled(Button as any)`
  max-width: 100%;
`

const CompletedJobsLink = styled(Link as any)`
  margin-left: 2rem;
  font-size: 1.2rem;
`

const HeaderContainer = styled(FlexContainer as any)`
  @media (max-width: ${props => props.theme.screenSize.mobile}) {
    justify-content: space-between;
  }
`

const FilterNotice = styled.div`
  width: 100%;
  text-align: center;
  background-color: ${props => props.theme.colors.yellow};
  padding: 8px 0;
  font-size: 14px;
  margin-top: 8px;
`

function FilterActiveBanner({ show }: { show: boolean }) {
  const intl = useIntl()
  const formatMessage = formatMessageSafe(intl)
  return show ? (
    <FilterNotice>
      <Text>{formatMessage(messages.filterActiveBanner)}</Text>
    </FilterNotice>
  ) : null
}

function getFiltersFromPreferences(filters: any) {
  if (!filters) return {}

  const filtered = filters.visibility ? Object.entries(filters.visibility).filter(([_, value]) => value) : []
  const visibility = filtered.map(([key]) => key)

  const jobTypes = filters?.jobTypes?.filter((item: any) => item.include).map((item: any) => item.name) || []
  const stations = filters?.stations?.filter((item: any) => item.include).map((item: any) => item.id) || []

  return {
    visibility,
    labels: jobTypes,
    stations: stations,
  }
}

const TowingJobsPage: React.FunctionComponent = () => {
  const { state: { currentUser } } = useContext(StateStore)
  const settings = useSettings()
  const { preferences: userPreferences, loading: preferencesLoading } = usePreferences()
  const historyPush = useHistoryPush()
  const intl = useIntl()
  const formatMessage = formatMessageSafe(intl)
  const translateMessage = useCallback(
    (messageId: string) => formatMessage({ id: messageId }),
    [formatMessage],
  )

  const filterState = useTowingJobFilters({
    currentUser: currentUser ?? null,
    options: { filters: getFiltersFromPreferences(userPreferences?.filters) },
    ready: !!settings && !preferencesLoading,
    onSetValues: setLocalStorageTowingJobFilters,
    onResetValues: removeLocalStorageTowingJobFilters,
  })

  const debouncedValues = useDebounce(filterState?.values, 300)
  const { data, loading, refetch, networkStatus } = useQuery<TowingJobsResponse>(TOWING_JOBS_SEARCH_QUERY, {
    variables: debouncedValues,
    fetchPolicy: 'cache-and-network',
    skip: !debouncedValues,
    notifyOnNetworkStatusChange: true,
  })

  const allIds = useMemo(
    () => [
      ...(data?.towingOrders?.towingOrders?.map(o => o.id) ?? []),
      ...(data?.towingRecords?.towingRecords?.map(r => r.id) ?? []),
    ],
    [data],
  )
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set())
  const allExpanded = allIds.length > 0 && expandedIds.size === allIds.length

  const handleExpandRow = useCallback((id: number) => {
    setExpandedIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }, [])

  const handleToggleAll = useCallback((checked: boolean) => {
    setExpandedIds(checked ? new Set(allIds) : new Set())
  }, [allIds])

  const { repairShops } = useRepairShops({ skip: !settings?.modules?.autoliitto || (!data && !loading) })

  return (
    <Page noPadding>
      {/* Needed for playwright tests */}
      {settings?.modules?.useExtendedRoutes && (
        <div data-testid="extended-routes-enabled" style={{ display: 'none' }} />
      )}
      <Row space="double" marginBottom="0" paddingMobile='0.5em 1.75em'>
        <Column paddingMobile='0'>
          <HeaderContainer>
            <Heading3>{formatMessage(messages.heading)}</Heading3>
            <CompletedJobsLink to="/towing-jobs/completed">{formatMessage(messages.completedHeading)}</CompletedJobsLink>
          </HeaderContainer>
        </Column>
        <ButtonContainer style={{ gap: 6, width: '100%', marginTop: 6, padding: '0 0em' }}>
          <AddButton
            buttonWidth='100%'
            category="new"
            label={formatMessage(messages.newTowingOrderButton)}
            dataTestId="new-towing-order-button"
            onClick={() => historyPush('/towing-order/add')}
          />
          {settings?.modules?.sos && (
            <AddButton
              category="new"
              label={formatMessage(messages.newSosButton)}
              dataTestId="new-sos-button"
              onClick={() => historyPush('/towing-order/add/sos')}
            />
          )}
        </ButtonContainer>
      </Row>

      <Row space='double' marginMobile='0' paddingMobile='0 1.75em'>
        <HeaderContainer zIndex="8" justifyContent="space-between">
          <TowingJobFilter
            show
            currentUser={currentUser!}
            resetToDefaultOptions={filterState?.resetToDefaultOptions ?? (() => {})}
            loading={!filterState}
            isDefaultModified={filterState?.isDefaultModified ?? false}
            values={filterState?.values ?? filterInitialValues}
            setValues={filterState?.setValues ?? (() => {})}
          >
            <ToggleAll show checked={allExpanded} onChange={handleToggleAll} />
            <FilterModal>
              {filterState && (
                <>
                  <JobDayFilter />
                  <VisibilityFilter />
                  <StatusFilter />
                  <JobTypeFilter options={userPreferences?.filters?.jobTypes.map((item: any) => ({ label: item.name, value: item.name })) || []} />
                  <StationFilter options={userPreferences?.filters?.stations.map((item: any) => ({ label: item.name, value: item.id })) || []} />
                  <PriorityFilter />
                  <OperatorFilter
                    options={settings?.operators.map(operator => ({ label: operator.name, value: operator.id })) || []}
                  />
                  <ActionButtons isDefaultModified={filterState.isDefaultModified} />
                </>
              )}
            </FilterModal>
          </TowingJobFilter>
        </HeaderContainer>
      </Row>

      <FilterActiveBanner show={filterState?.isDefaultModified ?? false} />

      <TowingJobsList
        data={data}
        loading={loading}
        currentUser={currentUser || undefined}
        settings={settings ?? undefined}
        repairShops={repairShops}
        translateMessage={translateMessage}
        skip={!debouncedValues}
        expandedIds={expandedIds}
        onExpandRow={handleExpandRow}
        refetch={refetch}
        isInitialLoad={networkStatus === 1}
      />
     
    </Page>
  )
}

export default TowingJobsPage
